import React from "react";
import {Box, SxProps, SystemStyleObject} from "@mui/system";
import {Paper} from "@mui/material";
import Logo from "../components/Logo";
import { theme } from "../theme";

const desktopRule = theme.breakpoints.up('md');

/* Wrapper elements of logo and dialog cover the entire available
* screen space when they are present as a standalone elements
* When Logo and Dialog elements are both present at a time,
* Dialog gets 50% margin right and Logo gets 50% margin left.
* This way we can easily apply transition on margin-left property for logo
* */
const wrapperStyleDesktop: SxProps = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    height: "100%",
};

const wrapperStyleMobile: SxProps = {
    position: 'unset',
    height: 'auto',

    [desktopRule]: {
        ...wrapperStyleDesktop
    },
};

const rootStyle: SxProps = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    flexDirection: "column",

    "& .LogoLayout-Dialog-wrapper": {
        ...wrapperStyleMobile,
        display: "flex",
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        transition: 'opacity 500ms',
        flex: 2,
        marginRight: 'unset',
        width: '100%',

        [desktopRule]: {
            marginRight: '50%',
            width: 'auto',
            ...wrapperStyleDesktop
        }
    },

    "& .LogoLayout-Paper": {
        maxWidth: 'unset',
        minHeight: 240,
        height: '100%',
        padding: 3,
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        justifyContent: "space-between",
        borderRadius: 0,

        [desktopRule]: {
            height: 600,
            maxWidth: 500,
            padding: 4,
            borderRadius: 1
        }
    },


    "& .LogoLayout-Logo-wrapper": {
        ...wrapperStyleMobile,
        display: 'flex',
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'margin-left 1s',

        [desktopRule]: {
            ...wrapperStyleDesktop
        }

    },

    "& > .LogoLayout-Logo": {
        display: "flex",
        maxWidth: 400,
        minWidth: 200,
        flexGrow: 1,
        justifyContent: 'center',
        alignContent: 'center',
        flexWrap: 'wrap',


        "& > svg": {
            margin: "24px 15%",
            width: 1.0,
        },
    },

    "& .LogoLayout-footer": {
        display: 'flex',
        height: "auto",
        alignItems: 'flex-end',
        paddingBottom: '50px',

        [desktopRule]: {
            height: '100%',
        }
    }


};

const logoStyle: SystemStyleObject = {
    display: "flex",
    maxWidth: 'unset',
    minWidth: 200,
    flexGrow: 1,
    justifyContent: 'center',
    alignContent: 'center',
    flexWrap: 'wrap',
    flexShrink: 1,
    paddingLeft: 0,
    paddingRight: 0,

    [desktopRule]: {
        maxWidth: 400,
        paddingLeft: 2,
        paddingRight: 2,
    }


};

interface LogoLayoutProps {
    dialogContent?: React.ReactNode | null;
    footerContent?: React.ReactNode;
    shortLogo?: boolean;
    flexDirection?: 'row'|'column';
}



export default function LogoLayout({ dialogContent, footerContent, shortLogo=false, flexDirection='row' }: LogoLayoutProps) {
    const dialogWrapperStyle: SxProps  = {
        //TODO - Fix fade out by keeping dialogContent mounted.
        opacity: dialogContent ? 1: 0,
        [desktopRule]: {
            flexDirection
        },
    };

    const logoWrapperStyle: SxProps =  {
        [desktopRule]: {
            marginLeft: dialogContent?  '50%': 0
        }
    };


    return (
        <Box sx={rootStyle}>
            <Box className="LogoLayout-Logo-wrapper" sx={logoWrapperStyle}>
                <Logo small={shortLogo} sx={logoStyle}/>
            </Box>
            <Box className="LogoLayout-Dialog-wrapper" sx={dialogWrapperStyle}>
                { dialogContent &&
                    <Paper className="LogoLayout-Paper" elevation={12}>
                        {dialogContent}
                    </Paper>
                }
            </Box>
            { footerContent &&
                <Box className="LogoLayout-footer">
                    {footerContent}
                </Box>
            }
        </Box>
    );
}
